/**
 * routeTemplate.ts — maps concrete App-Router pathnames to their route template.
 *
 * Derived statically from src/app directory dynamic segments (no runtime FS walk).
 * Pure, deterministic, zero deps, safe to import in both client and server bundles.
 *
 * route-template labels only — concrete segment values (orgId, clusterId,
 * inviteToken, project_slug, table_name, branch_name) are NEVER emitted.
 *
 */

// ---------------------------------------------------------------------------
// Route table — ordered most-specific to least-specific so the first match wins.
// Derived by hand from the src/app dynamic segments below; keep in sync as routes change:
//   invite/[orgId]/[inviteToken]
//   dashboard/orgs/[orgId]/clusters/[clusterId]/configs
//   dashboard/orgs/[orgId]/clusters/[clusterId]/delete
//   dashboard/orgs/[orgId]/clusters/[clusterId]
//   dashboard/orgs/[orgId]/clusters/new
//   dashboard/orgs/[orgId]/clusters
//   dashboard/orgs/[orgId]/audits
//   dashboard/orgs/[orgId]/configs
//   dashboard/orgs/[orgId]/users
//   dashboard/orgs/[orgId]
//   dashboard/orgs
//   dashboard/projects/[project_slug]/tables/[table_name]/branches/[branch_name]
//   dashboard/projects/[project_slug]/tables/[table_name]
//   dashboard/projects/[project_slug]/clusters
//   dashboard/projects/[project_slug]/jobs
//   dashboard/projects/[project_slug]/manifests
//   dashboard/projects/[project_slug]/sql
//   dashboard/projects/[project_slug]/tables
//   dashboard/projects/[project_slug]
//   dashboard/databases
//   dashboard/user/profile
//   dashboard
//   sign-in/[[...sign-in]]
//   sign-up
// ---------------------------------------------------------------------------

interface RoutePattern {
  /** RegExp matching the concrete pathname (anchored, no trailing slash). */
  re: RegExp;
  /** Template string to emit (no concrete segment values). */
  template: string;
}

const ROUTE_PATTERNS: readonly RoutePattern[] = [
  // invite
  {
    re: /^\/invite\/[^/]+\/[^/]+$/,
    template: "/invite/[orgId]/[inviteToken]",
  },

  // dashboard/orgs/:orgId/clusters/:clusterId subtree — most-specific first
  {
    re: /^\/dashboard\/orgs\/[^/]+\/clusters\/[^/]+\/configs$/,
    template: "/dashboard/orgs/[orgId]/clusters/[clusterId]/configs",
  },
  {
    re: /^\/dashboard\/orgs\/[^/]+\/clusters\/[^/]+\/delete$/,
    template: "/dashboard/orgs/[orgId]/clusters/[clusterId]/delete",
  },
  {
    re: /^\/dashboard\/orgs\/[^/]+\/clusters\/[^/]+$/,
    template: "/dashboard/orgs/[orgId]/clusters/[clusterId]",
  },
  {
    re: /^\/dashboard\/orgs\/[^/]+\/clusters\/new$/,
    template: "/dashboard/orgs/[orgId]/clusters/new",
  },
  {
    re: /^\/dashboard\/orgs\/[^/]+\/clusters$/,
    template: "/dashboard/orgs/[orgId]/clusters",
  },
  {
    re: /^\/dashboard\/orgs\/[^/]+\/audits$/,
    template: "/dashboard/orgs/[orgId]/audits",
  },
  {
    re: /^\/dashboard\/orgs\/[^/]+\/configs$/,
    template: "/dashboard/orgs/[orgId]/configs",
  },
  {
    re: /^\/dashboard\/orgs\/[^/]+\/users$/,
    template: "/dashboard/orgs/[orgId]/users",
  },
  {
    re: /^\/dashboard\/orgs\/[^/]+$/,
    template: "/dashboard/orgs/[orgId]",
  },
  {
    re: /^\/dashboard\/orgs$/,
    template: "/dashboard/orgs",
  },

  // dashboard/projects/:project_slug subtree — most-specific first
  {
    re: /^\/dashboard\/projects\/[^/]+\/tables\/[^/]+\/branches\/[^/]+$/,
    template:
      "/dashboard/projects/[project_slug]/tables/[table_name]/branches/[branch_name]",
  },
  {
    re: /^\/dashboard\/projects\/[^/]+\/tables\/[^/]+$/,
    template: "/dashboard/projects/[project_slug]/tables/[table_name]",
  },
  {
    re: /^\/dashboard\/projects\/[^/]+\/clusters$/,
    template: "/dashboard/projects/[project_slug]/clusters",
  },
  {
    re: /^\/dashboard\/projects\/[^/]+\/jobs\/[^/]+\/workers\/[^/]+$/,
    template:
      "/dashboard/projects/[project_slug]/jobs/[job_id]/workers/[worker_id]",
  },
  {
    re: /^\/dashboard\/projects\/[^/]+\/jobs\/[^/]+$/,
    template: "/dashboard/projects/[project_slug]/jobs/[job_id]",
  },
  {
    re: /^\/dashboard\/projects\/[^/]+\/jobs$/,
    template: "/dashboard/projects/[project_slug]/jobs",
  },
  {
    re: /^\/dashboard\/projects\/[^/]+\/manifests$/,
    template: "/dashboard/projects/[project_slug]/manifests",
  },
  {
    re: /^\/dashboard\/projects\/[^/]+\/sql$/,
    template: "/dashboard/projects/[project_slug]/sql",
  },
  {
    re: /^\/dashboard\/projects\/[^/]+\/tables$/,
    template: "/dashboard/projects/[project_slug]/tables",
  },
  {
    re: /^\/dashboard\/projects\/[^/]+$/,
    template: "/dashboard/projects/[project_slug]",
  },

  // dashboard misc
  { re: /^\/dashboard\/databases$/, template: "/dashboard/databases" },
  { re: /^\/dashboard\/user\/profile$/, template: "/dashboard/user/profile" },
  { re: /^\/dashboard$/, template: "/dashboard" },

  // auth
  { re: /^\/sign-in(\/.*)?$/, template: "/sign-in/[[...sign-in]]" },
  { re: /^\/sign-up$/, template: "/sign-up" },

  // root
  { re: /^\/$/, template: "/" },
];

/**
 * The bounded set of route templates this module can emit: every ROUTE_PATTERNS
 * template plus the `/unknown` sentinel. Server-side validators (the /rum relay)
 * fold any incoming route_template not in this set to `/unknown`, so an untrusted
 * browser beacon cannot mint arbitrary Prometheus series or leak raw paths/IDs
 * via the route_template label.
 */
export const VALID_ROUTE_TEMPLATES: ReadonlySet<string> = new Set(
  ROUTE_PATTERNS.map((p) => p.template).concat("/unknown"),
);

/**
 * Map a concrete Next.js App-Router pathname to its route template.
 *
 * @param pathname - `window.location.pathname` or `usePathname()` value.
 *   Query strings and hashes MUST be stripped before passing in.
 *   If not, they are stripped here defensively.
 * @returns A route template string. Unknown pathnames return `/unknown`.
 */
export function toRouteTemplate(pathname: string): string {
  // Defensive: strip query + hash even if the caller forgot.
  const clean = pathname.split("?")[0]!.split("#")[0]!;
  // Normalise: remove trailing slash (except root "/").
  const normalised = clean.length > 1 ? clean.replace(/\/$/, "") : clean;

  for (const { re, template } of ROUTE_PATTERNS) {
    if (re.test(normalised)) {
      return template;
    }
  }

  return "/unknown";
}
